import Immutable from 'immutable';

import {PASTE_DELTA} from '~/constants/CanvasConstants';
import {INK_COLORS} from '~/constants/InkConstants';
import DesignIconConstants from '~/constants/DesignIconConstants';
import {VIEW_ID_MAP} from '~/constants/ProductConstants';
import {designWithinConstraints, viewsFit} from '~/data/accessors/canvas';
import {isText, isArt, isUpload} from '~/data/accessors/icon';
import {roundDecimal} from '~/shared/utils/math';
import {difference, isSameColor} from '~/utils/colors';
import {getUnrotatedDimensions} from '~/shared/utils/math';
import {getImageDimensions} from '~/utils/images';
import {
  calculateConstraintsPixelsInches,
  constraintsMeaningfullyDifferent,
  getBiggestRect,
  getConstraintsDesignScaleRatio,
  getConstraintsScaleRatio,
  getDimensionsInches,
  getOffsetDesignCenter,
  getOffsetGuidelineCenter,
  getRatioForNInches,
  scaledDesignFitsNewConstraints
} from '~/utils/geometry';
import {generateImageUrl} from '~/shared/components/UploadImage';
import {buildUrl} from '~/utils/url';
import remoteServices from '~/constants/RemoteServices';

const DEFAULT_MIN_FONT_HEIGHT =
  DesignIconConstants.TEXT.DEFAULTS.MIN_FONT_HEIGHT;
const DEFAULT_MIN_CLIPART_HEIGHT =
  DesignIconConstants.CLIPART.DEFAULTS.MIN_ART_HEIGHT;
const MAX_TOP_GUIDELINE_DIMENSION = 7;
const MAX_TOP_GUIDELINE_DIMENSION_HOODIES = 5;

export function resizeArtIcon(icon, scaleX, scaleY) {
  const height = icon.get('height');
  const width = icon.get('width');
  const artMinHeight = DEFAULT_MIN_CLIPART_HEIGHT;
  const newScaleX = Math.max(scaleX, artMinHeight / width);
  const newScaleY = Math.max(scaleY, artMinHeight / height);
  const newHeight = roundDecimal(height * newScaleY, 2);
  const newWidth = roundDecimal(width * newScaleX, 2);
  icon = icon.set('height', newHeight).set('width', newWidth);
  if ((isArt(icon.type) || isUpload(icon.type)) && icon.lockRatio) {
    const {naturalWidth, naturalHeight} = icon;
    // const sinTheta = Math.abs(Math.sin(rotation));
    // const cosTheta = Math.abs(Math.cos(rotation));
    // const unrotatedHeight =
    //   (newHeight * cosTheta - newWidth * sinTheta) /
    //   (Math.pow(cosTheta, 2) - Math.pow(sinTheta, 2));
    // const unrotatedWidth =
    //   (-1 * (newHeight * sinTheta - newWidth * cosTheta)) /
    //   (Math.pow(cosTheta, 2) - Math.pow(sinTheta, 2));
    icon = icon
      .set('naturalWidth', naturalWidth * newScaleX)
      .set('naturalHeight', naturalHeight * newScaleY);
  }
  if (isText(icon.type) && textFitToDimension(icon)) {
    icon = icon.set('naturalWidth', newWidth).set('naturalHeight', newHeight);
  }
  return icon;
}

export function resizeTextIcon(icon, scale) {
  const fontHeight = icon.get('fontHeight') || 1;
  const fontMinHeight = icon.get('fontMinHeight') || DEFAULT_MIN_FONT_HEIGHT;
  const width = icon.get('width');
  const height = icon.get('height');
  const newScale = Math.max(scale, fontMinHeight / fontHeight);

  return icon.merge({
    fontHeight: roundDecimal(fontHeight * newScale),
    height: roundDecimal(height * newScale),
    width: roundDecimal(width * newScale)
  });
}

const spotColorMapper = (
  spotColors,
  original,
  user,
  changeSelectedUser = false
) => {
  return spotColors.map(spotColor => {
    if (isSameColor(spotColor.original, original)) {
      if (changeSelectedUser) {
        spotColor = spotColor.set('selectedUser', user);
      }
      return spotColor.set('user', user);
    }
    return spotColor;
  });
};

export function changeArtIconSpotColor(
  icon,
  original,
  user,
  transparentToggle = false
) {
  return icon
    .updateIn(['colors'], spotColors =>
      spotColorMapper(spotColors, original, user)
    )
    .updateIn(['color'], color => {
      const hasOnlyOneColor = icon.singleColor || icon.colors.size === 1;
      return hasOnlyOneColor && !transparentToggle ? user : color;
    })
    .updateIn(['transparent'], transparent => {
      if (isSameColor(original, INK_COLORS.White)) {
        return isSameColor(user, INK_COLORS.None);
      }

      return transparent;
    })
    .updateIn(['recolored'], () => true);
}

export function resetArtIconSpotColor(icon, original) {
  return changeArtIconSpotColor(icon, original, original).updateIn(
    ['recolored'],
    () => false
  );
}

export function changeSpotColor(
  icon,
  original,
  user,
  changeSelectedUser = false
) {
  return icon.updateIn(['spotColors'], spotColors =>
    spotColorMapper(spotColors, original, user, changeSelectedUser)
  );
}

export function removeSpotColor(icon, spotColor) {
  if (
    icon.backgroundColor &&
    isSameColor(icon.backgroundColor, spotColor.original)
  ) {
    icon = icon.set('backgroundColor', null);
  }
  return icon.updateIn(['spotColors'], spotColors => {
    return spotColors.filter(i => !isSameColor(i.original, spotColor.original));
  });
}

const iconColorAttributeMapper = {
  CLIPART: 'colors',
  UPLOAD: 'spotColors'
};
export function removeProductColor(
  icon,
  productColor,
  threshold,
  remove = true
) {
  return icon.updateIn([iconColorAttributeMapper[icon.type]], spotColors => {
    return spotColors.map(spotColor => {
      const colorDiff = difference(
        spotColor.selectedUser.hexString,
        productColor.hexString
      );

      if (colorDiff < threshold) {
        spotColor = remove
          ? spotColor.set('user', INK_COLORS.None)
          : spotColor.set('user', spotColor.selectedUser || spotColor.palette);
      }
      return spotColor;
    });
  });
}

export function addSpotColor(icon, spotColor) {
  return icon.updateIn(['spotColors'], spotColors => {
    return spotColors.push(spotColor);
  });
}

export function updateIcon(icon, keyOrConfig, value) {
  var newIcon;
  if (typeof keyOrConfig === 'object') {
    for (var keyName in keyOrConfig) {
      if (Object.prototype.hasOwnProperty.call(keyOrConfig, keyName)) {
        newIcon = (newIcon || icon).set(
          keyName,
          Immutable.fromJS(keyOrConfig[keyName])
        );
      }
    }
    return newIcon;
  } else {
    return icon.set(keyOrConfig, Immutable.fromJS(value));
  }
}

export function move(icon, deltaX, deltaY) {
  var newIcon = icon.update('x', x => x + roundDecimal(deltaX, 4));
  return newIcon.update('y', y => y + roundDecimal(deltaY, 4));
}

export function shift(icon, constraint, clipboardSource) {
  // don't shift icon(s) if pasting on the opposite view
  if (
    (icon.get('viewId') === VIEW_ID_MAP['front'] &&
      clipboardSource === VIEW_ID_MAP['back']) ||
    (icon.get('viewId') === VIEW_ID_MAP['back'] &&
      clipboardSource === VIEW_ID_MAP['front']) ||
    (icon.get('viewId') === VIEW_ID_MAP['rightSleeve'] &&
      clipboardSource === VIEW_ID_MAP['leftSleeve']) ||
    (icon.get('viewId') === VIEW_ID_MAP['leftSleeve'] &&
      clipboardSource === VIEW_ID_MAP['rightSleeve'])
  ) {
    return move(icon, 0, 0);
  }

  const currentX = icon.get('x');
  const currentY = icon.get('y');
  const adjustmentX = icon.get('width') / 2 + PASTE_DELTA;
  const adjustmentY = icon.get('height') / 2 + PASTE_DELTA;

  const shiftX =
    currentX + adjustmentX > constraint.width / 2
      ? currentX - adjustmentX < -constraint.width / 2
        ? 0
        : -PASTE_DELTA
      : PASTE_DELTA;
  const shiftY =
    currentY + adjustmentY > constraint.height / 2
      ? currentY - adjustmentY < -constraint.height / 2
        ? 0
        : -PASTE_DELTA
      : PASTE_DELTA;

  return move(icon, shiftX, shiftY);
}

export function resize(icon, scaleX, scaleY) {
  if (isText(icon.type) && !textFitToDimension(icon)) {
    return resizeTextIcon(icon, scaleX).set('sizeIsDirty', true);
  } else {
    return resizeArtIcon(icon, scaleX, scaleY);
  }
}

export function centerIcon(icon) {
  return move(icon, -icon.get('x'), -icon.get('y'));
}

export function moveByHalfDimension(icon) {
  const deltaX = icon.width / 2;
  const deltaY = icon.height / 2;
  return move(icon, deltaX, deltaY);
}

function repositionAlongAxis(iconPosition, iconSize, constraintSize) {
  const constraintRange = {start: constraintSize / -2, end: constraintSize / 2};
  const iconRange = {
    start: iconPosition - iconSize / 2,
    end: iconPosition + iconSize / 2
  };
  if (iconRange.start < constraintRange.start) {
    return constraintRange.start + iconSize / 2;
  } else if (iconRange.end > constraintRange.end) {
    return constraintRange.end - iconSize / 2;
  }
  return iconPosition;
}

export function repositionWithinConstraint(icon, constraint) {
  return updateIcon(icon, {
    x: repositionAlongAxis(icon.x, icon.width, constraint.width),
    y: repositionAlongAxis(icon.y, icon.height, constraint.height)
  });
}

export function fitWithinConstraint(
  icon,
  constraint,
  reposition = repositionWithinConstraint
) {
  if (typeof constraint === 'undefined') {
    return icon;
  }

  const constraintScale = Math.min(
    constraint.width / icon.get('width'),
    constraint.height / icon.get('height')
  );

  if (constraintScale < 1) {
    icon = resize(icon, constraintScale, constraintScale);
  }

  return reposition(icon, constraint);
}

export function place(icon, ratio) {
  return icon.set('x', icon.x * ratio).set('y', icon.y * ratio);
}

export function moveToCenter(icon, {x, y}) {
  return icon.set('x', icon.x - x).set('y', icon.y - y);
}

export const textFitToDimension = icon => {
  const {fit} = icon;
  return fit === 'occupy';
};

const getOriginalDimension = async icon => {
  if (isUpload(icon.type) || isArt(icon.type)) {
    const url = isUpload(icon.type)
      ? generateImageUrl(icon)
      : buildUrl(`${remoteServices.manipulate}/manipulate`, {
          clipart_id: icon.clipartId
        });
    return getImageDimensions(url);
  }
};

export const setNaturalDimensionsForArtAndUploads = async icons => {
  const promises = Object.values(icons.toJS())
    .filter(({type}) => isArt(type) || isUpload(type))
    .map(async icon => {
      const {width, height, rotation, id} = icon;
      if (rotation) {
        const {originalWidth, originalHeight} =
          await getOriginalDimension(icon);
        const {naturalWidth, naturalHeight} = getUnrotatedDimensions({
          width,
          height,
          rotation,
          originalHeight,
          originalWidth
        });
        icons = icons.setIn([id, 'naturalWidth'], naturalWidth);
        icons = icons.setIn([id, 'naturalHeight'], naturalHeight);
      } else {
        icons = icons.setIn([id, 'naturalWidth'], width);
        icons = icons.setIn([id, 'naturalHeight'], height);
      }
    });

  await Promise.all(promises);

  return icons;
};

export function iconRearranger({
  icon,
  designDimensions,
  oldConstraints,
  newConstraints,
  oldScales,
  newScales,
  guidelines,
  decorationMethod,
  styleType,
  changedProductCategory,
  useSpecialGuideline = true
}) {
  if (
    !icon?.viewId ||
    !designDimensions ||
    !oldConstraints ||
    !newConstraints ||
    !oldScales ||
    !newScales
  ) {
    return icon;
  }

  // Resize the icon to fit the new constraints
  // currentDesignDimensions is the dimensions of the whole design relative to the canvas left/top & width/height
  const currentDesignDimensions = designDimensions[icon.viewId];
  const oldConstraint = oldConstraints[icon.viewId];
  const newConstraint = newConstraints[icon.viewId];
  const oldScale = oldScales[icon.viewId];
  const newScale = newScales[icon.viewId];
  const guidelinesNewConstraint = guidelines ? guidelines[icon.viewId] : null;

  // Calculate the deltaX (width) and deltaY (height) to avoid possible issues
  // related to a mismatch of mms constraint data where the values of width or height
  // doesn't match the calculated right/bottom.
  const oldConstraintsInfo = calculateConstraintsPixelsInches({
    rect: oldConstraint,
    printScaleFactor: oldScale
  });
  const newConstraintsInfo = calculateConstraintsPixelsInches({
    rect: newConstraint,
    printScaleFactor: newScale
  });
  const designInfo = calculateConstraintsPixelsInches({
    rect: currentDesignDimensions,
    printScaleFactor: oldScale
  });
  const meaningfullyDiffConstraints = constraintsMeaningfullyDifferent({
    oldConstraintsInfo,
    newConstraintsInfo
  });
  const fitsMap = viewsFit(designDimensions, newConstraints);
  const boundingBoxWithinConstraints = designWithinConstraints(
    designDimensions,
    newConstraints
  );

  // meaningfullyDiffConstraints is used because any design created on a small design area, for instance
  // the Front of "Rotate USB Flash Drive 4GB", will fit into a larger constraint, such as the Front or LC of the
  // "Hanes Authentic T-shirt". Therefore, if we don't resize it, we'll end up with a really small design.
  if (
    !meaningfullyDiffConstraints &&
    fitsMap[icon.viewId] &&
    boundingBoxWithinConstraints[icon.viewId]
  ) {
    return icon;
  }

  // Handle special cases
  const {
    selectedGuideline,
    selectedGuidelineInfo,
    usingSelectedGuideline,
    optimizeForScreenPrintApparel
  } = getGuidelineInfoForSpecialCases({
    icon,
    newConstraintsInfo,
    guidelinesNewConstraint,
    newScale,
    decorationMethod,
    styleType,
    changedProductCategory
  });

  // Determines the optimal scale ratio based on the constraints, scale factors, and the design dimensions.
  // This value will be used to resize and position each icon of the design within the new constraints.
  const ratio = getDesignConstraintScaleRatio({
    designDimensions: currentDesignDimensions,
    oldConstraint,
    oldConstraintsInfo,
    newConstraint: useSpecialGuideline
      ? selectedGuideline || newConstraint
      : newConstraint,
    newConstraintsInfo: useSpecialGuideline
      ? selectedGuidelineInfo || newConstraintsInfo
      : newConstraintsInfo,
    designInfo,
    usingGuideline: useSpecialGuideline ? usingSelectedGuideline : false,
    optimizeForScreenPrintApparel,
    changedProductCategory
  });

  // Resize the icon
  const resizedIcon = resize(icon, ratio, ratio);

  // Apply the chosen ratio to translate the resizedIcon coordinates
  const shiftedIcon = place(resizedIcon, ratio);

  // Get the projected icon position based on the center of the constraint
  const iconConstraintCenter = moveToCenter(
    shiftedIcon,
    getOffsetDesignCenter({designInfo, ratio})
  );

  if (useSpecialGuideline && usingSelectedGuideline) {
    // Get the projected icon position based on the center of the selected guideline
    return moveToCenter(
      iconConstraintCenter,
      getOffsetGuidelineCenter({
        guidelineInfo: selectedGuidelineInfo,
        newConstraintsInfo,
        newScale
      })
    );
  }
  return iconConstraintCenter;
}

export function getGuidelineInfoForSpecialCases({
  icon,
  newConstraintsInfo,
  guidelinesNewConstraint,
  newScale,
  decorationMethod,
  styleType
}) {
  // Special cases:
  // 1 - If LC guideline is available for SP apparel items (polos, t-shirts, etc):
  //     Keep the original proportion of the design size & place in LC
  const leftChestGuideline = guidelinesNewConstraint?.find(
    guideline => guideline.name === 'Left Chest'
  );
  const leftChestGuidelineInfo = leftChestGuideline
    ? calculateConstraintsPixelsInches({
        rect: leftChestGuideline,
        printScaleFactor: newScale
      })
    : null;

  // 2 - Try the RC guideline if available
  const rightChestGuideline = guidelinesNewConstraint?.find(
    guideline => guideline.name === 'Right Chest'
  );
  const rightChestGuidelineInfo = rightChestGuideline
    ? calculateConstraintsPixelsInches({
        rect: rightChestGuideline,
        printScaleFactor: newScale
      })
    : null;

  // 3 - Check if there isn't any other guideline and the 'No Bleed' one exists
  const noBleedGuideline = guidelinesNewConstraint?.find(
    guideline => guideline.name === 'No Bleed'
  );
  const noBleedGuidelineInfo = noBleedGuideline
    ? calculateConstraintsPixelsInches({
        rect: noBleedGuideline,
        printScaleFactor: newScale
      })
    : null;

  // 4 - Create an imaginary guideline to place the design at the top of the constraint
  // in case there isn't another important guideline
  const topGuideline = {...newConstraintsInfo};
  topGuideline.name = 'Top Center';
  topGuideline.bottom =
    topGuideline.top +
    Math.min(topGuideline.deltaY * 0.8, MAX_TOP_GUIDELINE_DIMENSION * newScale);
  const topGuidelineInfo = calculateConstraintsPixelsInches({
    rect: topGuideline,
    printScaleFactor: newScale
  });

  let selectedGuideline =
    leftChestGuideline || rightChestGuideline || noBleedGuideline;
  let selectedGuidelineInfo =
    leftChestGuidelineInfo || rightChestGuidelineInfo || noBleedGuidelineInfo;

  // If the new product is a screen printed apparel and there is no guideline,
  // proporionally resize the design to 7" in width or height and place within the topGuideline
  const optimizeForScreenPrintApparel =
    icon.viewId === VIEW_ID_MAP['front'] &&
    decorationMethod === 'screen' &&
    styleType === 'apparel' &&
    !selectedGuideline;

  if (optimizeForScreenPrintApparel) {
    selectedGuideline = topGuideline;
    selectedGuidelineInfo = topGuidelineInfo;
  }

  // 5 - Get the biggest guideline
  const biggestConstraint = getBiggestRect(guidelinesNewConstraint);
  const biggestGuideline =
    biggestConstraint?.name !== 'Front' ? biggestConstraint : null;
  const biggestGuidelineInfo = biggestGuideline
    ? calculateConstraintsPixelsInches({
        rect: biggestGuideline,
        printScaleFactor: newScale
      })
    : null;

  if (!selectedGuideline && biggestGuideline) {
    selectedGuideline = biggestGuideline;
    selectedGuidelineInfo = biggestGuidelineInfo;
  }

  const usingSelectedGuideline = !!selectedGuideline;

  return {
    selectedGuideline,
    selectedGuidelineInfo,
    usingSelectedGuideline,
    optimizeForScreenPrintApparel
  };
}

export function getDesignConstraintScaleRatio({
  designDimensions,
  oldConstraint,
  oldConstraintsInfo,
  newConstraint,
  newConstraintsInfo,
  designInfo,
  usingGuideline = false,
  optimizeForScreenPrintApparel = false,
  changedProductCategory
}) {
  if (
    !designDimensions ||
    !oldConstraint ||
    !oldConstraintsInfo ||
    !newConstraint ||
    !newConstraintsInfo ||
    !designInfo
  ) {
    return 1;
  }

  const scaledConstraintsDimensions = getDimensionsInches(newConstraintsInfo);
  const maxPercentageFullDimension = usingGuideline ? 0.94 : 1;
  const fullWidthRatio = getConstraintsDesignScaleRatio({
    constraintsDimensions: scaledConstraintsDimensions,
    designDimensions,
    dimension: 'width',
    dimensionPercentage: maxPercentageFullDimension
  });
  const fullHeightRatio = getConstraintsDesignScaleRatio({
    constraintsDimensions: scaledConstraintsDimensions,
    designDimensions,
    dimension: 'height',
    dimensionPercentage: maxPercentageFullDimension
  });
  const widthRatio = getConstraintsScaleRatio({
    newConstraint,
    oldConstraint,
    dimension: 'width'
  });
  const heightRatio = getConstraintsScaleRatio({
    newConstraint,
    oldConstraint,
    dimension: 'height'
  });
  const designByWidthFitsConstraint = scaledDesignFitsNewConstraints({
    designDimensions,
    scaledConstraintsDimensions,
    ratio: widthRatio
  });
  const designByHeightFitsConstraint = scaledDesignFitsNewConstraints({
    designDimensions,
    scaledConstraintsDimensions,
    ratio: heightRatio
  });

  // If the new product is a screen printed apparel, currently displaying the front view,
  // and there's no special guideline, then optimize the ratio to resize the design to 7" in width or height
  if (optimizeForScreenPrintApparel) {
    const screenPrintDimensionSize =
      changedProductCategory?.toLowerCase?.() === 'hoodies'
        ? MAX_TOP_GUIDELINE_DIMENSION_HOODIES
        : MAX_TOP_GUIDELINE_DIMENSION;

    const ratio7InchesWidth = getRatioForNInches({
      fullDimensionInches: scaledConstraintsDimensions.width,
      fullDimensionRatio: fullWidthRatio,
      desiredValInches: screenPrintDimensionSize
    });
    const ratio7InchesHeight = getRatioForNInches({
      fullDimensionInches: scaledConstraintsDimensions.height,
      fullDimensionRatio: fullHeightRatio,
      desiredValInches: screenPrintDimensionSize
    });
    const ratio7InchesWidthFits = scaledDesignFitsNewConstraints({
      designDimensions,
      scaledConstraintsDimensions,
      ratio: ratio7InchesWidth
    });
    const ratio7InchesHeightFits = scaledDesignFitsNewConstraints({
      designDimensions,
      scaledConstraintsDimensions,
      ratio: ratio7InchesHeight
    });

    if (ratio7InchesWidthFits && ratio7InchesHeightFits) {
      return Math.max(ratio7InchesWidth, ratio7InchesHeight);
    }

    if (ratio7InchesWidthFits || ratio7InchesHeightFits) {
      return Math.min(ratio7InchesWidth, ratio7InchesHeight);
    }

    return Math.min(fullWidthRatio, fullHeightRatio);
  }

  const resizingDown =
    newConstraintsInfo?.areaInches < oldConstraintsInfo?.areaInches;
  const standardRatio = Math.min(fullWidthRatio, fullHeightRatio);
  const maxNaturalRatio = Math.max(widthRatio, heightRatio);
  const minNaturalRatio = Math.min(widthRatio, heightRatio);

  if (
    resizingDown &&
    designByWidthFitsConstraint &&
    designByHeightFitsConstraint &&
    maxNaturalRatio > standardRatio * 0.85
  ) {
    return maxNaturalRatio;
  }

  if (
    resizingDown &&
    (designByWidthFitsConstraint || designByHeightFitsConstraint) &&
    minNaturalRatio > standardRatio * 0.85
  ) {
    return minNaturalRatio;
  }

  if (
    !resizingDown &&
    designByWidthFitsConstraint &&
    designByHeightFitsConstraint &&
    maxNaturalRatio > standardRatio * 0.5
  ) {
    return maxNaturalRatio;
  }

  if (
    !resizingDown &&
    (designByWidthFitsConstraint || designByHeightFitsConstraint) &&
    minNaturalRatio > standardRatio * 0.5
  ) {
    return minNaturalRatio;
  }

  return Math.min(fullWidthRatio, fullHeightRatio);
}
